import React, { useMemo } from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslations } from './BookingRecord.i18n';
import { TextEllipsisWrapper } from '@shared/utils/formatting/textEllipsis';
import { StarRating } from '@shared/components';
import { useAccommodationsContext } from '../../context/useAccommodationsContext';
import { BookingRecordProps } from '../GuestHotelBookingsList/GuestHotelBookingsList.types';
import { Skeleton } from '@shared/components/Skeleton';
import { boxShadowValuesV2 } from '@shared/utils/style/dropShadows';
import { useTranslation } from '@shared/core';
import { useFormattedDate } from '@shared/hooks/useFormattedDate';
import { useFormattedDateRange } from '@shared/hooks/useFormattedDateRange';
import { isValid } from 'date-fns';

interface HotelInfoProps {
  id: string;
  hotelName: string;
  hotelImage: string;
  starRating?: number | null;
  distanceFromVenue: string;
}

const HotelInfo: React.FC<HotelInfoProps> = ({ id, hotelName, hotelImage, starRating, distanceFromVenue }) => {
  return (
    <Flex
      gap={5}
      alignItems="flex-start"
      width="100%"
    >
      <Box
        width={pxToRem(80)}
        height={pxToRem(80)}
        borderRadius={3}
        backgroundImage={`url(${hotelImage})`}
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        flexShrink={0}
      />
      <Flex
        flexDirection="column"
        gap={1}
        alignItems="flex-start"
      >
        <TextEllipsisWrapper lineClamp={2}>
          <TextV2
            fontSize={pxToRem(18)}
            fontWeight={600}
            typographyVariant="button2"
          >
            {hotelName}
          </TextV2>
        </TextEllipsisWrapper>

        <Flex
          alignItems="center"
          gap={1}
          alignSelf="flex-start"
        >
          {starRating ? (
            <Flex
              alignItems="center"
              gap={2}
            >
              <StarRating
                id={id}
                rating={starRating}
                showTooltip={false}
              />
            </Flex>
          ) : null}
          <TextV2
            typographyVariant="body1"
            color="mono8"
          >
            {distanceFromVenue}
          </TextV2>
        </Flex>
      </Flex>
    </Flex>
  );
};

interface GuestInfoProps {
  guestName: string;
  guestEmail: string;
}

const GuestInfo: React.FC<GuestInfoProps> = ({ guestName, guestEmail }) => {
  return (
    <Flex
      flexDirection="column"
      alignItems="flex-start"
      width="100%"
      paddingX={5}
      paddingY={4}
      borderRadius="8px 8px 0px 0px"
      border="1px solid"
      borderColor="mono3"
      borderBottom="none"
    >
      <Flex
        flexDirection="column"
        gap={pxToRem(2)}
      >
        <TextEllipsisWrapper>
          <TextV2
            typographyVariant="body1"
            fontWeight={600}
            color="mono14"
          >
            {guestName}
          </TextV2>
        </TextEllipsisWrapper>
        <TextEllipsisWrapper>
          <TextV2
            typographyVariant="body1"
            color="mono8"
          >
            {guestEmail}
          </TextV2>
        </TextEllipsisWrapper>
      </Flex>
    </Flex>
  );
};

interface BookingDetailsProps {
  dateRange: string | null | undefined;
}

const BookingDetails: React.FC<BookingDetailsProps> = ({ dateRange }) => {
  return (
    <Flex
      alignItems="flex-start"
      width="100%"
      border="1px solid"
      borderColor="mono3"
      borderRadius="0px 0px 8px 8px"
      paddingY={4}
      paddingX={5}
      flex={1}
    >
      <TextV2
        typographyVariant="body1"
        color="mono14"
        fontWeight={500}
      >
        {dateRange}
      </TextV2>
    </Flex>
  );
};

interface BookingTimestampProps {
  bookedOnTimestamp: string | null;
}

const BookingTimestamp: React.FC<BookingTimestampProps> = ({ bookedOnTimestamp }) => {
  return (
    <TextV2
      alignSelf="flex-start"
      typographyVariant="label2"
      color="mono12"
      fontSize={pxToRem(14)}
      textAlign="left"
    >
      {bookedOnTimestamp}
    </TextV2>
  );
};

export const BookingRecordSkeleton = () => {
  return (
    <Skeleton
      width={pxToRem(400)}
      height={pxToRem(348)}
      borderRadius="8px"
      boxShadow={boxShadowValuesV2.xxxl}
    />
  );
};

export const BookingRecord: React.FC<BookingRecordProps> = props => {
  const { getBookingRecordTranslations } = useTranslations();
  const bookingRecordTrans = getBookingRecordTranslations();
  const { venueData: venue } = useAccommodationsContext();
  // PR Comment #2: Use existing travelMap translation file, not websiteEditor
  const { tq } = useTranslation('travelMap');

  // Format dates at component level using hooks
  const { bookingInfo } = props || {};
  const maybeCheckInDate = bookingInfo?.checkInDate ? new Date(bookingInfo.checkInDate) : null;
  const checkInDate = maybeCheckInDate && isValid(maybeCheckInDate) ? maybeCheckInDate : null;
  const maybeCheckOutDate = bookingInfo?.checkOutDate ? new Date(bookingInfo.checkOutDate) : null;
  const checkOutDate = maybeCheckOutDate && isValid(maybeCheckOutDate) ? maybeCheckOutDate : null;

  const dateRange = useFormattedDateRange(checkInDate, checkOutDate);

  // Format: "November 13, 2025" (closest existing format without time)
  const maybeBookingDate = bookingInfo?.bookingDate ? new Date(bookingInfo.bookingDate) : null;
  const bookingDate = maybeBookingDate && isValid(maybeBookingDate) ? maybeBookingDate : null;
  const formattedBookingDate = useFormattedDate(bookingDate, 'MMMM dd, yyyy');

  const { bookingId, hotelName, hotelImageUrl, starRating, distanceFromVenue, guestName, guestEmail, bookedOnTimestamp } = useMemo(() => {
    const { joyPlace } = props || {};

    // All data fixes at single point instead of in jsx
    const bookingId = bookingInfo.id;
    const hotelName = bookingRecordTrans.translateValue(joyPlace.name);
    const hotelImageUrl = joyPlace.photo.url;
    const starRating = joyPlace.starRating;
    const distanceFromVenue = bookingRecordTrans.distanceFromVenue(venue, joyPlace.location);
    const guestName = bookingRecordTrans.guestName(bookingInfo.firstName, bookingInfo.lastName);
    const guestEmail = bookingRecordTrans.translateValue(bookingInfo.email);

    const bookedOnTimestamp = formattedBookingDate ? tq`Booked on ${formattedBookingDate}` : null;

    return { bookingId, hotelName, hotelImageUrl, starRating, distanceFromVenue, guestName, guestEmail, bookedOnTimestamp };
  }, [bookingRecordTrans, props, venue, tq, formattedBookingDate, bookingInfo]);

  return (
    <Flex
      width={pxToRem(400)}
      background="white"
      flexDirection="column"
      alignItems="center"
      padding={6}
      gap={5}
      borderRadius="8px"
      boxShadow={boxShadowValuesV2.xxxl}
    >
      <HotelInfo
        id={bookingId}
        hotelName={hotelName}
        hotelImage={hotelImageUrl}
        starRating={starRating}
        distanceFromVenue={distanceFromVenue}
      />
      <Box width="100%">
        <GuestInfo
          guestName={guestName}
          guestEmail={guestEmail}
        />
        <BookingDetails dateRange={dateRange} />
      </Box>
      <BookingTimestamp bookedOnTimestamp={bookedOnTimestamp} />
    </Flex>
  );
};
